import { useEffect, useRef, useState } from "react";
import { Play, Pause, Headphones } from "lucide-react";

const MeditationPlayer = ({ states }) => {
  const [active, setActive] = useState(null); // index playing
  const ctxRef = useRef(null);
  const nodesRef = useRef([]);

  const stop = () => {
    nodesRef.current.forEach((n) => { try { n.stop && n.stop(); n.disconnect(); } catch { /* noop */ } });
    nodesRef.current = [];
    setActive(null);
  };

  const play = (i, s) => {
    if (active === i) { stop(); return; }
    stop();
    if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = ctxRef.current;
    if (ctx.state === "suspended") ctx.resume();

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.6);
    gain.connect(ctx.destination);

    const makeSide = (freq, pan) => {
      const osc = ctx.createOscillator();
      const panner = ctx.createStereoPanner();
      osc.type = "sine";
      osc.frequency.value = freq;
      panner.pan.value = pan;
      osc.connect(panner);
      panner.connect(gain);
      osc.start();
      return osc;
    };

    const left = makeSide(s.carrier, -1);
    const right = makeSide(s.carrier + s.beat, 1);
    nodesRef.current = [left, right, gain];
    setActive(i);
  };

  useEffect(() => () => stop(), []);

  return (
    <div className="space-y-3" data-testid="meditation-player">
      <p className="text-xs text-ink-mute flex items-center gap-1.5"><Headphones className="w-3.5 h-3.5" /> Use headphones for the binaural effect. Start at a low volume and breathe slowly.</p>
      {states.map((s, i) => (
        <div key={i} className={`rounded-2xl border px-4 py-4 transition-colors ${active === i ? "bg-brand text-linen border-brand" : "bg-white border-linen-3"}`} data-testid={`meditation-${i}`}>
          <div className="flex items-center gap-3">
            <button onClick={() => play(i, s)} data-testid={`meditation-play-${i}`}
              className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${active === i ? "bg-linen/20" : "bg-calm"}`}>
              {active === i ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 text-brand" />}
            </button>
            <div className="flex-1 min-w-0">
              <p className={`font-medium ${active === i ? "text-linen" : "text-ink"}`}>{s.name}</p>
              <p className={`text-xs ${active === i ? "text-linen/80" : "text-ink-mute"}`}>{s.beat} Hz beat · {s.carrier} Hz carrier</p>
            </div>
            {active === i && <span className="text-xs text-linen/90 animate-pulse">playing…</span>}
          </div>
          {active === i && <p className="text-sm text-linen/90 leading-relaxed mt-3 italic">{s.guide}</p>}
        </div>
      ))}
    </div>
  );
};

export default MeditationPlayer;
